import { Spinner } from "react-bootstrap";
import { FaChartLine, FaChevronDown, FaChevronUp, FaFileExport, FaUtensils } from "react-icons/fa6";
import { FaEdit, FaTimes, FaSave } from "react-icons/fa";
import { formatSalesValue } from "../../../utils/formatUtils";
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useStock } from "../../../context/StockProvider";
import { FiTrash2 } from 'react-icons/fi';
import Select from "react-select";
import {
    formatQuantity,
    getUnitInfo,
    shouldShowSecondaryUnit,
    computeQuantityFromPrimarySecondary,
    splitQuantityToPrimarySecondary,
} from "../../../utils/quantityConversion";
import { selectStyles } from "../../../utils/cssUtils";

// ── Actual-cost accuracy helpers ────────────────────────────────────────────
const toNumber = (val) => parseFloat(val) || 0;

const getActualConsumptionTotal = (items = []) =>
    items.reduce((sum, item) => sum + toNumber(item.actual_consumption_cost), 0);

const calculatePercentage = (part, total) => {
    if (!total || total === 0) return null;
    return ((Math.abs(part) / total) * 100).toFixed(2);
};

const EditablePhysicalCountTable = ({
    items,
    stockItems,
    editedItems,
    onOpeningPrimaryChange,
    onOpeningSecondaryChange,
    onClosingPrimaryChange,
    onClosingSecondaryChange,
    handleDeleteItem
}) => {
    return (
        <div className="rounded-md border overflow-y-auto max-h-[550px] tiny-scrollbar">
            <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                <thead className="bg-gray-100 sticky top-0 z-20">
                    <tr>
                        <th rowSpan={2} className="px-2 py-2.5 text-xs font-medium text-gray-700 uppercase tracking-wider">Group</th>
                        <th rowSpan={2} className="px-2 py-2.5 text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                        <th rowSpan={2} className="px-2 py-2.5 text-xs font-medium text-gray-700 uppercase tracking-wider">Unit Definition</th>

                        <th colSpan={4} className="px-2 py-2.5 text-xs font-medium text-gray-700 uppercase tracking-wider border-l">
                            Opening Count
                        </th>

                        <th colSpan={4} className="px-2 py-2.5 text-xs font-medium text-gray-700 uppercase tracking-wider border-l">
                            Closing Count
                        </th>
                    </tr>
                    <tr>
                        <th
                            colSpan={2}
                            className="px-2 py-1.5 text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-l"
                        >
                            Primary
                        </th>
                        <th
                            colSpan={2}
                            className="px-2 py-1.5 text-xs font-medium text-gray-700 uppercase tracking-wider"
                        >
                            Secondary
                        </th>

                        <th
                            colSpan={2}
                            className="px-2 py-1.5 text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-l"
                        >
                            Primary
                        </th>
                        <th
                            colSpan={2}
                            className="px-2 py-1.5 text-xs font-medium text-gray-700 uppercase tracking-wider"
                        >
                            Secondary
                        </th>
                    </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-100">
                    {items.length === 0 && (
                        <tr>
                            <td colSpan={11} className="py-6 text-center text-sm text-gray-500">
                                No items match your search
                            </td>
                        </tr>
                    )}

                    {items.map((item, index) => {
                        const unitInfo = getUnitInfo(item.item_name, stockItems);
                        const showSecondary = shouldShowSecondaryUnit(unitInfo);
                        const edited = editedItems[item.id] || {};

                        return (
                            <tr key={item.id ?? index} className="hover:bg-gray-50">
                                <td className="p-2">{item.group_name}</td>
                                <td className="p-2">{item.item_name}</td>
                                <td className="p-2 whitespace-nowrap">
                                    {unitInfo.unitdefinition
                                        ? `${unitInfo.unitdefinition.unit} (${unitInfo.unitdefinition.uom})`
                                        : "N/A"}
                                </td>

                                {/* Opening count — primary */}
                                <td className="p-2 border-l">
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={edited.openingPrimaryQty ?? ""}
                                        className="w-20 px-2 py-1 border rounded text-center"
                                        onChange={(e) =>
                                            onOpeningPrimaryChange(item.id, e.target.value)
                                        }
                                    />
                                </td>
                                <td className="p-2 text-gray-700">
                                    {unitInfo.primary_unit || "N/A"}
                                </td>

                                {/* Opening count — secondary */}
                                <td className="p-2 border-l">
                                    {showSecondary ? (
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={edited.openingSecondaryQty ?? ""}
                                            className="w-20 px-2 py-1 border rounded text-center"
                                            onChange={(e) =>
                                                onOpeningSecondaryChange(item.id, e.target.value)
                                            }
                                        />
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td className="p-2 text-gray-700">
                                    {showSecondary ? unitInfo.secondary_unit || "N/A" : "-"}
                                </td>

                                {/* Closing count — primary */}
                                <td className="p-2 border-l">
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={edited.closingPrimaryQty ?? ""}
                                        className="w-20 px-2 py-1 border rounded text-center"
                                        onChange={(e) =>
                                            onClosingPrimaryChange(item.id, e.target.value)
                                        }
                                    />
                                </td>
                                <td className="p-2 text-gray-700">
                                    {unitInfo.primary_unit || "N/A"}
                                </td>

                                {/* Closing count — secondary */}
                                <td className="p-2 border-l">
                                    {showSecondary ? (
                                        <input
                                            type="number"
                                            min="0"
                                            step="any"
                                            value={edited.closingSecondaryQty ?? ""}
                                            className="w-20 px-2 py-1 border rounded text-center"
                                            onChange={(e) =>
                                                onClosingSecondaryChange(item.id, e.target.value)
                                            }
                                        />
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td className="p-2 text-gray-700">
                                    {showSecondary ? unitInfo.secondary_unit || "N/A" : "-"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const InventoryHistoryTable = ({
    reportData,
    expandedReports,
    reportDetails,
    groupItemsByType,
    toggleReportExpansion,
    formattedDate,
    loadingDetails,
    handleCostingNavigation,
    handleExport,
    loading,
    token,
    fetchReportData,
    setExpandedReports,
    setReportDetails,
    getReportKey
}) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { stockItems } = useStock()

    const [editingReportId, setEditingReportId] = useState(null);
    const [editedItems, setEditedItems] = useState({});
    const [originalItems, setOriginalItems] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Selected item search option per report+type, e.g. "reportKey::Food".
    const [itemSearchByKey, setItemSearchByKey] = useState({});

    const latestReportKey = useMemo(() => {
        if (!reportData.snapshot_reports?.length) return null;

        const sorted = [...reportData.snapshot_reports].sort((a, b) => {
            const dateA = new Date(a.food_created_at || a.beverage_created_at || 0);
            const dateB = new Date(b.food_created_at || b.beverage_created_at || 0);
            return dateB - dateA; // newest first
        });

        return getReportKey(sorted[0]);
    }, [reportData.snapshot_reports, getReportKey]);

    const getReportItemsFor = (reportId, type) =>
        reportDetails[reportId]?.fnb_costing_reports
            ?.find(r => r.report_for === type)
            ?.items_actual_costing || [];

    const handleEditClick = (reportId, type) => {
        setEditingReportId(reportId);
        setEditingType(type);

        const reportItems = getReportItemsFor(reportId, type);

        const initialEdits = {};

        reportItems.forEach(item => {
            const unitInfo = getUnitInfo(item.item_name, stockItems);

            const { primaryQty: openingPrimaryQty, secondaryQty: openingSecondaryQty } =
                splitQuantityToPrimarySecondary(item.opening_count, unitInfo);

            const { primaryQty: closingPrimaryQty, secondaryQty: closingSecondaryQty } =
                splitQuantityToPrimarySecondary(item.physical_count, unitInfo);

            initialEdits[item.id] = {
                openingPrimaryQty,
                openingSecondaryQty,
                closingPrimaryQty,
                closingSecondaryQty,
            };
        });

        setEditedItems(initialEdits);
        setOriginalItems(
            Object.fromEntries(
                Object.entries(initialEdits).map(([id, values]) => [id, { ...values }])
            )
        );
    };

    const handleCancelEdit = () => {
        setEditingReportId(null);
        setEditingType(null);
        setEditedItems({});
        setOriginalItems({});
    };

    const updateEditedItem = (itemId, patch) => {
        setEditedItems(prev => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                ...patch,
            },
        }));
    };

    const handleOpeningPrimaryChange = (itemId, value) =>
        updateEditedItem(itemId, { openingPrimaryQty: value });

    const handleOpeningSecondaryChange = (itemId, value) =>
        updateEditedItem(itemId, { openingSecondaryQty: value });

    const handleClosingPrimaryChange = (itemId, value) =>
        updateEditedItem(itemId, { closingPrimaryQty: value });

    const handleClosingSecondaryChange = (itemId, value) =>
        updateEditedItem(itemId, { closingSecondaryQty: value });

    const handleSaveChanges = async () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these changes?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                setIsSaving(true);

                                const reportItems = getReportItemsFor(editingReportId, editingType);

                                const updates = reportItems
                                    .filter(item => editedItems[item.id])
                                    .map(item => {
                                        const unitInfo = getUnitInfo(item.item_name, stockItems);
                                        const edited = editedItems[item.id];
                                        const original = originalItems[item.id] || {};

                                        const openingChanged =
                                            edited.openingPrimaryQty !== original.openingPrimaryQty ||
                                            edited.openingSecondaryQty !== original.openingSecondaryQty;

                                        const closingChanged =
                                            edited.closingPrimaryQty !== original.closingPrimaryQty ||
                                            edited.closingSecondaryQty !== original.closingSecondaryQty;

                                        // Skip items where neither side changed at all.
                                        if (!openingChanged && !closingChanged) return null;

                                        const update = {
                                            snapshot_detail_id: parseInt(item.id),
                                        };

                                        if (closingChanged) {
                                            update.physical_count = computeQuantityFromPrimarySecondary(
                                                edited.closingPrimaryQty,
                                                edited.closingSecondaryQty,
                                                unitInfo
                                            ) || 0;
                                        }

                                        if (openingChanged) {
                                            update.opening_count = computeQuantityFromPrimarySecondary(
                                                edited.openingPrimaryQty,
                                                edited.openingSecondaryQty,
                                                unitInfo
                                            ) || 0;
                                        }

                                        return update;
                                    })
                                    .filter(Boolean);

                                if (updates.length === 0) {
                                    toast.info("No changes to save");
                                    setIsSaving(false);
                                    setEditingReportId(null);
                                    setEditingType(null);
                                    setEditedItems({});
                                    setOriginalItems({});
                                    return;
                                }

                                await axios.post(
                                    `${baseUrl}/update-physical-counts-${editingType.toLowerCase()}`,
                                    {
                                        token,
                                        updates
                                    }
                                );

                                toast.success("Physical counts updated successfully");
                                setExpandedReports([]);
                                setReportDetails([]);
                                fetchReportData();
                            } catch (error) {
                                toast.error(error?.response?.data?.error || error?.response?.data?.message || "Failed to update physical counts");
                                console.error("Update error:", error);
                            } finally {
                                setIsSaving(false);
                                setEditingReportId(null);
                                setEditingType(null);
                                setEditedItems({});
                                setOriginalItems({});
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'update-physical-stocks'
            }
        );
    };

    const handleDeleteItem = async (item, itemType, reportKey) => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to delete {item.item_name}?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                setIsDeleting(true);

                                await axios.post(
                                    `${baseUrl}/delete-inventory-snapshot-item-${itemType.toLowerCase()}`,
                                    {
                                        token,
                                        item_id: item.id
                                    }
                                );

                                setReportDetails(prev => {
                                    const reportEntry = prev[reportKey];
                                    if (!reportEntry) return prev;

                                    const updatedFnbReports = reportEntry.fnb_costing_reports.map(r =>
                                        r.report_for === itemType
                                            ? {
                                                ...r,
                                                items_actual_costing: r.items_actual_costing.filter(
                                                    (i) => i.id !== item.id
                                                ),
                                            }
                                            : r
                                    );

                                    return {
                                        ...prev,
                                        [reportKey]: {
                                            ...reportEntry,
                                            fnb_costing_reports: updatedFnbReports,
                                        },
                                    };
                                });

                                toast.success(`${item.item_name} deleted successfully`);
                            } catch (error) {
                                toast.error(error?.response?.data?.error || error?.response?.data?.message || "Failed to delete item");
                                console.error("Update error:", error);
                            } finally {
                                setIsDeleting(false);
                            }
                        }}
                    >
                        Delete
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'delete-physical-item'
            }
        );
    }

    return (
        <div className="rounded-md border">
            <table className="min-w-full overflow-y-auto divide-y divide-gray-200 text-sm text-center">
                <thead className="bg-gray-100 uppercase tracking-wider">
                    {/* TOP HEADER */}
                    <tr>
                        <th rowSpan="2" className="p-2 text-xs font-medium text-gray-700">Created At</th>
                        <th rowSpan="2" className="p-2 text-xs font-medium text-gray-700">Start Date</th>
                        <th rowSpan="2" className="p-2 text-xs font-medium text-gray-700">End Date</th>

                        {/* GROUPED FOOD */}
                        <th colSpan="3" className="p-2 text-xs font-medium text-gray-700">
                            Food Items
                        </th>

                        {/* GROUPED BEVERAGE */}
                        <th colSpan="3" className="p-2 text-xs font-medium text-gray-700">
                            Beverage Items
                        </th>

                        <th rowSpan="2" className="p-2 text-xs font-medium text-gray-700">Actions</th>
                    </tr>

                    {/* SUB HEADER */}
                    <tr>
                        {/* Food sub columns */}
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Total</th>
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Missing</th>
                        <th className="p-2 text-xs font-medium text-gray-700">Accuracy</th>

                        {/* Beverage sub columns */}
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Total</th>
                        <th className="p-2 text-xs font-medium border-r text-gray-700">Missing</th>
                        <th className="p-2 text-xs font-medium text-gray-700">Accuracy</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {reportData.snapshot_reports?.length > 0 ? (
                        reportData.snapshot_reports.map((report) => {
                            const reportKey = getReportKey(report);
                            const isExpanded = expandedReports.includes(reportKey);
                            const isLatestReport = reportKey === latestReportKey;
                            const details = reportDetails[reportKey];

                            const items =
                                details?.fnb_costing_reports?.flatMap(report =>
                                    (report.items_actual_costing || []).map(item => ({
                                        ...item,
                                        type: report.report_for
                                    }))
                                ) || [];

                            const groupedItems = groupItemsByType(items);

                            return (
                                <>
                                    <tr
                                        key={reportKey}
                                        className='hover:bg-gray-50 hover:cursor-pointer'
                                        onClick={() => toggleReportExpansion(report)}
                                    >
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.food_created_at || report.beverage_created_at)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.start_date)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {formattedDate(report.end_date)}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.food_total_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.food_missing_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {report.food_accuracy || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.beverage_total_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap border-r">
                                            {report.beverage_missing_items || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            {report.beverage_accuracy || 0}
                                        </td>
                                        <td className="p-3 whitespace-nowrap">
                                            <button className="text-gray-500 hover:text-gray-700">
                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </button>
                                        </td>
                                    </tr>

                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={10} className="p-3 bg-gray-50 shadow-md">
                                                {loadingDetails[reportKey] ? (
                                                    <div className="text-center my-4">
                                                        <Spinner animation="border" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </Spinner>
                                                    </div>
                                                ) : details ? (
                                                    <>
                                                        <div className="flex gap-3 mb-2">
                                                            <button
                                                                onClick={() => handleCostingNavigation(report)}
                                                                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md"
                                                            >
                                                                <FaChartLine className="h-3 w-3" />
                                                                <FaUtensils className="h-3 w-3" />
                                                                View Costing Analysis
                                                            </button>

                                                            <button
                                                                onClick={() => handleExport(report)}
                                                                className="flex items-center gap-1 px-3 py-1.5 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md"
                                                            >
                                                                <FaFileExport className="h-3 w-3" />
                                                                Export
                                                            </button>
                                                        </div>

                                                        <div>
                                                            {Object.entries(groupedItems).map(([type, items]) => {
                                                                const isEditing =
                                                                    editingReportId === reportKey &&
                                                                    editingType === type;

                                                                const searchKey = `${reportKey}::${type}`;
                                                                const selectedItemOption = itemSearchByKey[searchKey] || null;

                                                                const itemOptions = items.map((item) => ({
                                                                    value: item.id,
                                                                    label: item.item_name,
                                                                }));

                                                                const displayedItems = selectedItemOption
                                                                    ? items.filter((item) => item.id === selectedItemOption.value)
                                                                    : items;

                                                                const reportForType =
                                                                    details?.fnb_costing_reports?.find(
                                                                        (r) => r.report_for === type
                                                                    ) || {};

                                                                const actualCostAccuracy = (Number(reportForType?.cost_of_goods_sold) / Number(reportForType?.sales_summary?.net_sale) * 100).toFixed(2)

                                                                return (
                                                                    <div key={type} className="mb-4">
                                                                        <div className="flex justify-between mb-3 items-end">
                                                                            <h3 className="text-lg font-semibold mb-0 leading-none">{type}</h3>

                                                                            <div className="flex items-center gap-3">
                                                                                <div className="w-64">
                                                                                    <Select
                                                                                        options={itemOptions}
                                                                                        styles={selectStyles}
                                                                                        isClearable
                                                                                        placeholder="Search item..."
                                                                                        value={selectedItemOption}
                                                                                        onChange={(option) =>
                                                                                            setItemSearchByKey((prev) => ({
                                                                                                ...prev,
                                                                                                [searchKey]: option,
                                                                                            }))
                                                                                        }
                                                                                    />
                                                                                </div>

                                                                                {!isEditing ? (
                                                                                    isLatestReport ? (
                                                                                        <button
                                                                                            onClick={() => handleEditClick(reportKey, type)}
                                                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 hover:shadow-md"
                                                                                        >
                                                                                            <FaEdit className="h-3 w-3" />
                                                                                            Edit
                                                                                        </button>
                                                                                    ) : (
                                                                                        <span
                                                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                                            title="Only the latest snapshot can be edited"
                                                                                        >
                                                                                            <FaEdit className="h-3 w-3" />
                                                                                            Edit
                                                                                        </span>
                                                                                    )
                                                                                ) : (
                                                                                    <>
                                                                                        <button
                                                                                            onClick={handleSaveChanges}
                                                                                            disabled={isSaving}
                                                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-green-100 text-green-800 hover:bg-green-200 hover:shadow-md disabled:opacity-50"
                                                                                        >
                                                                                            {isSaving ? (
                                                                                                <Spinner animation="border" size="sm" />
                                                                                            ) : (
                                                                                                <>
                                                                                                    <FaSave className="h-3 w-3" />
                                                                                                    Save Changes
                                                                                                </>
                                                                                            )}
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={handleCancelEdit}
                                                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 hover:shadow-md"
                                                                                        >
                                                                                            <FaTimes className="h-3 w-3" />
                                                                                            Cancel
                                                                                        </button>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {isEditing ? (
                                                                            <EditablePhysicalCountTable
                                                                                items={displayedItems}
                                                                                stockItems={stockItems}
                                                                                editedItems={editedItems}
                                                                                onOpeningPrimaryChange={handleOpeningPrimaryChange}
                                                                                onOpeningSecondaryChange={handleOpeningSecondaryChange}
                                                                                onClosingPrimaryChange={handleClosingPrimaryChange}
                                                                                onClosingSecondaryChange={handleClosingSecondaryChange}
                                                                            />
                                                                        ) : (
                                                                            <div className="rounded-md border overflow-y-auto max-h-[550px] tiny-scrollbar">
                                                                                <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                                                                    <thead className="bg-gray-100 sticky top-0 z-20">
                                                                                        <tr>
                                                                                            {[
                                                                                                "Group", "Item Name", "UOM", "Opening", "Received", "Wastage", "Consumption", "Closing", "Discrepancy", "Closing Amount", "Actions"
                                                                                            ].map((col, idx) => (
                                                                                                <th
                                                                                                    key={idx}
                                                                                                    className="px-2 py-2.5 text-xs font-medium whitespace-nowrap text-gray-700 uppercase tracking-wider"
                                                                                                >
                                                                                                    {col}
                                                                                                </th>
                                                                                            ))}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                                                        {displayedItems.length === 0 && (
                                                                                            <tr>
                                                                                                <td colSpan={11} className="py-6 text-center text-sm text-gray-500">
                                                                                                    No items match your search
                                                                                                </td>
                                                                                            </tr>
                                                                                        )}

                                                                                        {displayedItems.map((item, index) => (
                                                                                            <tr key={index} className="hover:bg-gray-50">
                                                                                                <td className="p-2">{item.group_name}</td>

                                                                                                <td className="p-2">{item.item_name}</td>

                                                                                                <td className="p-2">{item.uom}</td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.opening_count, false, item.item_name, stockItems)}
                                                                                                </td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.total_received, false, item.item_name, stockItems)}
                                                                                                </td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.wastage_count, false, item.item_name, stockItems)}
                                                                                                </td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.total_consumed, false, item.item_name, stockItems)}
                                                                                                </td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.physical_count, false, item.item_name, stockItems)}
                                                                                                </td>

                                                                                                <td className="p-2">
                                                                                                    {formatQuantity(item, item.discrepancy_count, false, item.item_name, stockItems)}
                                                                                                </td>
                                                                                                <td className="p-2">{formatSalesValue(item.physical_cost)}</td>
                                                                                                <td className="p-2">
                                                                                                    <button
                                                                                                        className="p-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 hover:shadow transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-100 disabled:hover:shadow-none"
                                                                                                        onClick={() => handleDeleteItem(item, type, reportKey)}
                                                                                                        disabled={isDeleting || !isLatestReport}
                                                                                                        title={isLatestReport ? "Delete" : "Only the latest snapshot can have items deleted"}
                                                                                                    >
                                                                                                        <FiTrash2 size={14} />
                                                                                                    </button>
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        )
                                                                        }

                                                                        <div className="w-full flex flex-wrap justify-between mt-2">
                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Purchase: Rs. {formatSalesValue(reportForType?.purchase_items?.summary?.total_value)}
                                                                            </div>

                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Opening: Rs. {formatSalesValue(reportForType?.opening)}
                                                                            </div>

                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Closing: Rs. {formatSalesValue(reportForType?.closing)}
                                                                            </div>

                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                COGS: Rs. {formatSalesValue(reportForType?.cost_of_goods_sold)}
                                                                            </div>

                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Net Sale: Rs. {formatSalesValue(reportForType?.sales_summary?.net_sale)}
                                                                            </div>

                                                                            <div className="px-3 py-2 bg-gray-200 font-medium text-gray-800 rounded-md">
                                                                                Actual Cost %: {actualCostAccuracy !== null ? `${actualCostAccuracy}%` : "N/A"}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center text-gray-500 py-4">
                                                        No details available
                                                    </div>
                                                )}
                                            </td>
                                        </tr >
                                    )}
                                </>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={10} className="px-3 py-4 text-gray-500">
                                {loading ? "Loading..." : "No data available"}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div >
    )
}

export default InventoryHistoryTable